import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import LandscapeIcon from '@mui/icons-material/Landscape';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DownloadIcon from '@mui/icons-material/Download';
import type { CompliancePreviewResponse, FilingRequirement } from '../../lib/api';

interface Props {
  report: CompliancePreviewResponse;
  explained?: boolean;
  onRestart: () => void;
  onExplain?: () => void;
  onExportCsv?: () => void;
}

const CATEGORY_META: Record<
  FilingRequirement['category'],
  { label: string; icon: React.ReactNode }
> = {
  federal: { label: 'Federal Requirements', icon: <LandscapeIcon /> },
  'state-solicitation': { label: 'Charitable Solicitation', icon: <LocationOnIcon /> },
  'state-corporate': { label: 'State Corporate Filings', icon: <BusinessIcon /> },
  'state-tax': { label: 'State Tax Exemptions', icon: <ReceiptLongIcon /> },
  fundraising: { label: 'Fundraising Compliance', icon: <VolunteerActivismIcon /> },
  employment: { label: 'Employment & Payroll', icon: <GroupsIcon /> },
  'certificate-of-authority': { label: 'Certificate of Authority', icon: <VerifiedUserIcon /> },
  audit: { label: 'Audit & CPA Reports', icon: <FactCheckIcon /> },
};

const CATEGORY_ORDER: FilingRequirement['category'][] = [
  'federal',
  'state-solicitation',
  'state-corporate',
  'certificate-of-authority',
  'state-tax',
  'audit',
  'fundraising',
  'employment',
];

// Derived from the category list rather than hand-placed, so a new filing
// category cannot silently go uncounted or blow out the row widths.
const STAT_TILES: Array<{ label: string; key: keyof CompliancePreviewResponse['counts'] }> = [
  { label: 'Total', key: 'total' },
  { label: 'Federal', key: 'federal' },
  { label: 'Solicitation', key: 'solicitation' },
  { label: 'Corporate', key: 'corporate' },
  { label: 'Cert. of Authority', key: 'certificateOfAuthority' },
  { label: 'Tax', key: 'tax' },
  { label: 'Audit', key: 'audit' },
  { label: 'Fundraising', key: 'fundraising' },
  { label: 'Employment', key: 'employment' },
];

const PRIORITY_META: Record<
  FilingRequirement['priority'],
  { label: string; color: 'error' | 'warning' | 'info' | 'success' }
> = {
  required: { label: 'Required', color: 'error' },
  likely: { label: 'Likely', color: 'warning' },
  conditional: { label: 'If applicable', color: 'info' },
  recommended: { label: 'Recommended', color: 'success' },
};

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    </Paper>
  );
}

function FilingCard({ filing }: { filing: FilingRequirement }) {
  const meta = PRIORITY_META[filing.priority];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {filing.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {filing.description}
          </Typography>
          {filing.reasons && filing.reasons.length > 0 && (
            <Box mt={1.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Triggered by:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5} useFlexGap>
                {filing.reasons.map((r, i) => (
                  <Chip key={i} size="small" variant="outlined" label={r} />
                ))}
              </Stack>
            </Box>
          )}
          <Stack direction="row" spacing={2} mt={1.5} flexWrap="wrap">
            {filing.deadline && (
              <Typography variant="caption" color="text.secondary">
                <strong>Deadline:</strong> {filing.deadline}
              </Typography>
            )}
            {filing.fee && (
              <Typography variant="caption" color="text.secondary">
                <strong>Fee:</strong> {filing.fee}
              </Typography>
            )}
            {filing.state && (
              <Typography variant="caption" color="text.secondary">
                <strong>State:</strong> {filing.state}
              </Typography>
            )}
          </Stack>
          {filing.actionUrl && (
            <Button
              href={filing.actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={<OpenInNewIcon />}
              sx={{ mt: 1.5, ml: -1 }}
            >
              {filing.actionLabel ?? 'Learn more'}
            </Button>
          )}
        </Box>
        <Chip label={meta.label} color={meta.color} size="small" />
      </Stack>
    </Paper>
  );
}

export default function ResultsView({
  report,
  explained,
  onRestart,
  onExplain,
  onExportCsv,
}: Props) {
  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    meta: CATEGORY_META[cat],
    filings: report.filings.filter((f) => f.category === cat),
  })).filter((g) => g.filings.length > 0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Compliance Lookup Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {report.orgTypeName} &middot; {report.date} &middot; computed in {report.responseTimeMs} ms
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {onExplain && !explained && (
            <Button variant="outlined" startIcon={<PsychologyIcon />} onClick={onExplain}>
              Explain reasons
            </Button>
          )}
          {onExportCsv && (
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportCsv}>
              Export CSV
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRestart}>
            Run another lookup
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="body1">{report.summary}</Typography>
      </Paper>

      <Grid container spacing={2} mb={4}>
        {STAT_TILES.map(({ label, key }) => (
          <Grid item xs={6} sm={3} md={12 / (STAT_TILES.length + 1)} key={label}>
            <StatCard label={label} value={key === 'total' ? report.counts.total : report.counts[key]} />
          </Grid>
        ))}
      </Grid>

      <Stack spacing={4}>
        {grouped.map((group) => (
          <Box key={group.category}>
            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}>{group.meta.icon}</Box>
              <Typography variant="h6" fontWeight={700}>
                {group.meta.label}
              </Typography>
              <Chip label={group.filings.length} size="small" />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              {group.filings.map((f, i) => (
                <FilingCard key={`${f.title}-${i}`} filing={f} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
